const API_URL = process.env.REACT_APP_BACKEND_URL || '';

class ApiService {
  constructor() {
    // Initialize token from localStorage on service creation
    this.token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  }

  setToken(token) {
    this.token = token;
    if (typeof window !== 'undefined') {
      if (token) {
        localStorage.setItem('token', token);
      } else {
        localStorage.removeItem('token');
      }
    }
  }

  getToken() {
    // Always try to get from localStorage if not in memory
    if (!this.token && typeof window !== 'undefined') {
      this.token = localStorage.getItem('token');
    }
    return this.token;
  }

  // Fix UTC timestamps from backend (Python datetime.utcnow() returns no 'Z' suffix)
  fixUtcTimestamps(data) {
    if (!data || typeof data !== 'object') return data;
    if (Array.isArray(data)) return data.map(item => this.fixUtcTimestamps(item));
    const result = { ...data };
    for (const key of Object.keys(result)) {
      const val = result[key];
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(val) && !val.endsWith('Z') && !val.includes('+')) {
        result[key] = val + 'Z';
      } else if (val && typeof val === 'object') {
        result[key] = this.fixUtcTimestamps(val);
      }
    }
    return result;
  }

  // Parse error detail from FastAPI (handles string, array of objects, or object)
  parseErrorDetail(detail) {
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(d => d.msg || d.message || JSON.stringify(d)).join('. ');
    }
    if (typeof detail === 'object') return detail.msg || detail.message || JSON.stringify(detail);
    return String(detail);
  }

  async request(endpoint, options = {}) {
    const url = `${API_URL}/api${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const currentToken = this.getToken();
    if (currentToken) {
      headers['Authorization'] = `Bearer ${currentToken}`;
    }

    try {
      const response = await fetch(url, { ...options, headers });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Request failed' }));
        throw new Error(this.parseErrorDetail(error.detail) || `Request failed: ${response.status}`);
      }

      const data = await response.json();
      return this.fixUtcTimestamps(data);
    } catch (err) {
      // Handle network errors (offline, CORS, etc.)
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        throw new Error('Network error. Please check your connection.');
      }
      throw err;
    }
  }

  // Auth
  async login(email, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async register(data) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createOrganization(data) {
    return this.request('/organizations/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMe() {
    return this.request('/auth/me');
  }

  // Users
  async getUsers() {
    return this.request('/users');
  }

  async getGuards() {
    return this.request('/users/guards');
  }

  async getSupervisors() {
    return this.request('/users/supervisors');
  }

  async createSupervisor(data) {
    return this.request('/users/create-supervisor', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createGuard(data) {
    return this.request('/users/create-guard', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deactivateUser(userId) {
    return this.request(`/users/${userId}`, { method: 'DELETE' });
  }

  async resetUserPassword(userId) {
    return this.request(`/users/${userId}/reset-password`, { method: 'PATCH' });
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }


  // Sites
  async createSite(data) {
    return this.request('/sites', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getSites() {
    return this.request('/sites');
  }

  async getSite(id) {
    return this.request(`/sites/${id}`);
  }

  async getSiteCheckpoints(siteId) {
    return this.request(`/sites/${siteId}/checkpoints`);
  }

  async deleteSite(id) {
    return this.request(`/sites/${id}`, { method: 'DELETE' });
  }

  getSiteQrPdfUrl(siteId) {
    return `${API_URL}/api/sites/${siteId}/qr-pdf`;
  }

  getAllSitesQrPdfUrl() {
    return `${API_URL}/api/organization/all-sites-qr-pdf`;
  }

  // Download helper for PDFs
  async downloadPdf(url, filename) {
    try {
      const token = this.getToken();
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, { headers });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Download failed' }));
        throw new Error(error.detail || 'Download failed');
      }
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      throw new Error(err.message || 'Failed to download PDF');
    }
  }

  // Download helper for CSV files
  async downloadCsv(endpoint, filename) {
    try {
      const token = this.getToken();
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      const response = await fetch(`${API_URL}/api${endpoint}`, { headers });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Download failed' }));
        throw new Error(error.detail || 'Download failed');
      }
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      throw new Error(err.message || 'Failed to download CSV');
    }
  }

  // Checkpoints
  async createCheckpoint(data) {
    return this.request('/checkpoints', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getCheckpoints(siteId) {
    return this.request(`/checkpoints${siteId ? `?site_id=${siteId}` : ''}`);
  }

  async deleteCheckpoint(id) {
    return this.request(`/checkpoints/${id}`, { method: 'DELETE' });
  }

  // Scans
  async createScan(data) {
    return this.request('/scans', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getScans(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.site_id) queryParams.append('site_id', params.site_id);
    if (params.guard_id) queryParams.append('guard_id', params.guard_id);
    const query = queryParams.toString();
    return this.request(`/scans${query ? `?${query}` : ''}`);
  }

  async getTodayScans() {
    return this.request('/scans/today');
  }

  // Incidents
  async createIncident(data) {
    return this.request('/incidents', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getIncidents(status) {
    return this.request(`/incidents${status ? `?status=${status}` : ''}`);
  }

  async getIncident(id) {
    return this.request(`/incidents/${id}`);
  }

  async updateIncident(id, data) {
    return this.request(`/incidents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Shifts
  async createShift(data) {
    return this.request('/shifts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getShifts(date) {
    return this.request(`/shifts${date ? `?date=${date}` : ''}`);
  }

  async getWeeklyShifts(startDate) {
    return this.request(`/shifts/weekly?start_date=${startDate}`);
  }

  async getMyTodayShift() {
    return this.request('/shifts/my-today');
  }

  async deleteShift(id) {
    return this.request(`/shifts/${id}`, { method: 'DELETE' });
  }

  async updateShift(shiftId, data) {
    return this.request(`/shifts/${shiftId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }


  async assignGuardToShift(shiftId, guardId) {
    return this.request(`/shifts/${shiftId}/assign`, {
      method: 'PATCH',
      body: JSON.stringify({ guard_id: guardId }),
    });
  }

  async unassignGuardFromShift(shiftId) {
    return this.request(`/shifts/${shiftId}/unassign`, {
      method: 'PATCH',
    });
  }

  // Attendance
  async getMyShiftsToday() {
    return this.request('/attendance/my-shifts-today');
  }

  async getMyShifts() {
    return this.request('/shifts/my-shifts');
  }

  async clockIn(data) {
    return this.request('/attendance/clock-in', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async clockOut(data) {
    return this.request('/attendance/clock-out', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAttendanceStatus() {
    return this.request('/attendance/status');
  }

  async getAttendance(guardId) {
    return this.request(`/attendance${guardId ? `?guard_id=${guardId}` : ''}`);
  }

  async getPendingApprovals() {
    return this.request('/attendance/pending-approvals');
  }

  async approveLateCheckin(attendanceId, status, notes, adjustedClockIn, adjustedClockOut) {
    const body = { status, notes };
    if (adjustedClockIn) body.adjusted_clock_in = adjustedClockIn;
    if (adjustedClockOut) body.adjusted_clock_out = adjustedClockOut;
    return this.request(`/attendance/${attendanceId}/approve`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  /**
   * New approval endpoint - approves either clock-in or clock-out separately
   * @param {string} attendanceId - The attendance record ID
   * @param {string} approvalType - 'clock_in' or 'clock_out'
   * @param {string} status - 'approved' or 'rejected'
   * @param {string} notes - Optional notes
   * @param {string} adjustedTime - Optional ISO timestamp for time adjustment
   */
  async approveAttendanceAction(attendanceId, approvalType, status, notes, adjustedTime) {
    const body = { 
      approval_type: approvalType,
      status, 
      notes 
    };
    if (adjustedTime) body.adjusted_time = adjustedTime;
    return this.request(`/attendance/${attendanceId}/approve-action`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async getPendingApprovalsCount() {
    return this.request('/attendance/late-checkins-count');
  }

  // Location
  async updateLocation(data) {
    return this.request('/location/update', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getGuardLocations() {
    return this.request('/location/guards');
  }

  // Dashboard
  async getDashboardStats() {
    return this.request('/dashboard/stats');
  }

  // Patrol Photos
  async createPatrolPhoto(data) {
    return this.request('/patrol-photos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPatrolPhotos(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.guard_id) queryParams.append('guard_id', params.guard_id);
    if (params.site_id) queryParams.append('site_id', params.site_id);
    const query = queryParams.toString();
    return this.request(`/patrol-photos${query ? `?${query}` : ''}`);
  }

  async getTodayPatrolPhotos() {
    return this.request('/patrol-photos/today');
  }

  // Notifications
  async getNotifications() {
    return this.request('/notifications');
  }

  async markNotificationRead(id) {
    return this.request(`/notifications/${id}/read`, { method: 'PATCH' });
  }

  async getUnreadCount() {
    return this.request('/notifications/unread-count');
  }

  // Organization
  async registerOrganization(data) {
    return this.request('/organizations/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getOrganization() {
    return this.request('/organizations/me');
  }

  async getOrganizationInfo() {
    return this.request('/organizations/info');
  }

  async regenerateInviteCode() {
    return this.request('/organizations/regenerate-invite-code', { method: 'POST' });
  }

  async requestToJoin(data) {
    return this.request('/organizations/join', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPendingJoinRequests() {
    return this.request('/organizations/pending-requests');
  }

  async handleJoinRequest(userId, action, notes) {
    return this.request(`/organizations/pending-requests/${userId}`, {
      method: 'POST',
      body: JSON.stringify({ action, notes }),
    });
  }

  // Reports
  async getShiftCompliance(period = 'week', guardId) {
    let url = `/reports/shift-compliance?period=${period}`;
    if (guardId) url += `&guard_id=${guardId}`;
    return this.request(url);
  }

  async getDetailedTimesheet(startDate, endDate, guardId, siteId) {
    let url = '/timesheet/detailed?';
    if (startDate) url += `start_date=${startDate}&`;
    if (endDate) url += `end_date=${endDate}&`;
    if (guardId) url += `guard_id=${guardId}&`;
    if (siteId) url += `site_id=${siteId}`;
    return this.request(url);
  }

  async exportTimesheetCSV(startDate, endDate, guardId, siteId) {
    let url = '/timesheet/export-csv?';
    if (startDate) url += `start_date=${startDate}&`;
    if (endDate) url += `end_date=${endDate}&`;
    if (guardId) url += `guard_id=${guardId}&`;
    if (siteId) url += `site_id=${siteId}`;
    const filename = `timesheet_${startDate || 'all'}_to_${endDate || 'all'}.csv`;
    return this.downloadCsv(url, filename);
  }

  getTimesheetPdfUrl(startDate, endDate, guardId, siteId) {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    let url = `${API_URL}/api/timesheet/export-pdf?`;
    if (startDate) url += `start_date=${startDate}&`;
    if (endDate) url += `end_date=${endDate}&`;
    if (guardId) url += `guard_id=${guardId}&`;
    if (siteId) url += `site_id=${siteId}`;
    return url;
  }

  async createBulkShifts(data) {
    return this.request('/shifts/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async exportPatrolCSV(startDate, endDate, guardId, siteId) {
    let url = '/patrol/export-csv?';
    if (startDate) url += `start_date=${startDate}&`;
    if (endDate) url += `end_date=${endDate}&`;
    if (guardId) url += `guard_id=${guardId}&`;
    if (siteId) url += `site_id=${siteId}`;
    const filename = `patrol_activity_${startDate || 'all'}_to_${endDate || 'all'}.csv`;
    return this.downloadCsv(url, filename);
  }

  getPatrolPDFUrl(startDate, endDate, guardId, siteId) {
    let url = `${API_URL}/api/patrol/export-pdf?`;
    if (startDate) url += `start_date=${startDate}&`;
    if (endDate) url += `end_date=${endDate}&`;
    if (guardId) url += `guard_id=${guardId}&`;
    if (siteId) url += `site_id=${siteId}`;
    return url;
  }

  // Activity Logs
  async getActivityLogs(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.action) queryParams.append('action', params.action);
    const query = queryParams.toString();
    return this.request(`/activity-logs${query ? `?${query}` : ''}`);
  }

  // Activity Timeline
  async getActivityTimeline(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.guard_id) queryParams.append('guard_id', params.guard_id);
    if (params.site_id) queryParams.append('site_id', params.site_id);
    if (params.activity_type) queryParams.append('activity_type', params.activity_type);
    if (params.limit) queryParams.append('limit', params.limit);
    const query = queryParams.toString();
    return this.request(`/activity-timeline${query ? `?${query}` : ''}`);
  }

  async exportActivityTimelineCSV(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.guard_id) queryParams.append('guard_id', params.guard_id);
    if (params.site_id) queryParams.append('site_id', params.site_id);
    const query = queryParams.toString();
    return this.request(`/activity-timeline/export-csv${query ? `?${query}` : ''}`);
  }

  // ==================== PLATFORM (SUPER ADMIN) ====================
  
  async getPlatformStats() {
    return this.request('/platform/stats');
  }

  async getPlatformOrganizations() {
    return this.request('/platform/organizations');
  }

  async getPlatformOrganization(orgId) {
    return this.request(`/platform/organizations/${orgId}`);
  }

  async updateOrganizationPlan(orgId, plan) {
    return this.request(`/platform/organizations/${orgId}/plan?plan=${plan}`, {
      method: 'PUT',
    });
  }

  async updateOrganizationStatus(orgId, isActive) {
    return this.request(`/platform/organizations/${orgId}/status?is_active=${isActive}`, {
      method: 'PUT',
    });
  }

  async updateOrganizationLimits(orgId, limits) {
    return this.request(`/platform/organizations/${orgId}/limits`, {
      method: 'PUT',
      body: JSON.stringify(limits),
    });
  }

  async createPaymentLink(orgId, amount, description, interval = 'month') {
    return this.request(`/platform/organizations/${orgId}/payment-link`, {
      method: 'POST',
      body: JSON.stringify({ amount, description, interval }),
    });
  }

  async updateSubscription(orgId, amount, reason, interval = 'month') {
    return this.request(`/platform/organizations/${orgId}/subscription`, {
      method: 'PUT',
      body: JSON.stringify({ amount, reason, interval }),
    });
  }

  async getOrganizationBilling(orgId) {
    return this.request(`/platform/organizations/${orgId}/billing`);
  }

  async getOrganizationPayments(orgId) {
    return this.request(`/platform/organizations/${orgId}/payments`);
  }

  async getPlatformPlans() {
    return this.request('/platform/plans');
  }
}

export const api = new ApiService();